import * as React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Typography } from '@mui/material';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import UsageStatisticsFiscalYear from './UsageStatisticsFiscalYear';
import UsageStatisticsPriorityPeriods from './UsageStatisticsPriorityPeriods';

import {
  HTTPHeaders
} from '../../interfaces';

import {
  API_URL,
  EMPTY_STRING
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';
// System hooks
import { useSystem } from '../../hooks/UseSystem';

const UsageStatistics: React.FC = () => {

  const { getAccessToken } = useAuth0User();
  const { getCurrentFiscalYear } = useSystem();

  const [activeTab, setActiveTab] = React.useState('1');
  const [fiscalYears, setFiscalYears] = React.useState<Array<number>>([]);
  const [fiscalYear, setFiscalYear] = React.useState<string>(EMPTY_STRING);

  // #region Styles

  const theme = useTheme();
  const styles = {
    root: {
      flexGrow: 1,
      padding: theme.spacing(1, 1, 1, 1)
    },
    tabContainer: {
      height: 500,
      margin: theme.spacing(0, 0, 0, 0),
      padding: theme.spacing(1, 0, 1, 1),
    },
    tabHeading: {
      borderBottom: 1,
      borderColor: 'divider'
    },
    toolsContainer: {
      flexGrow: 1,
      padding: theme.spacing(1, 0, 1, 0),
    },
  };

  // #endregion

  // #region Methods

  /*
  * Fetch list of fiscal years
  */
  const fetchFiscalYears = async () => {

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const params = new URLSearchParams();
    params.append('year', fiscalYear);

    const url = new URL(`${API_URL}/v1/requests/fiscalyears`);
    url.search = params.toString();

    await fetch(url.toString(),
      {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Fiscal years not retrieved');
        }
        return response.json()
      })
      .then((f: Array<number>) => {
        setFiscalYears(f);
      })
      .catch(err => {
        throw new Error('Fiscal years not retrieved');
      });
  }

  /*
  * Fetch list of fiscal years
  */
  const fiscalYearLabel = () => {
    return `5/1/${(Number(fiscalYear) - 1).toFixed(0)} - 4 /30/${fiscalYear}`;
  }

  // #endregion

  // #region Event handlers

  /*
   * Handle fiscal year change
   */
  const handleFiscalYearChange = (event: SelectChangeEvent) => {
    setFiscalYear(event.target.value as string);
  };

  /*
  * Handle tab change
  */
  const handleTabChange = (event: React.SyntheticEvent, tab: string) => {
    setActiveTab(tab);
  };

  React.useEffect(() => {
    // Default to current FY
    setFiscalYear(getCurrentFiscalYear());
    fetchFiscalYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // #endregion

  return (
    <Box sx={styles.root}>
      <Grid container alignItems="center">
        <Grid>
          <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
            <InputLabel id="fiscalYearsLabel">Fiscal Year</InputLabel>
            <Select
              labelId="fiscalYearsLabel"
              id="fiscalYears"
              value={fiscalYear}
              label="Fiscal Year"
              onChange={handleFiscalYearChange}
            >
              {fiscalYears.map((gt: number) => (
                <MenuItem key={gt} value={gt}>{gt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid>
          <Typography variant="overline">{fiscalYearLabel()}</Typography>
        </Grid>
      </Grid>
      <TabContext value={activeTab}>
        <Box sx={styles.tabHeading}>
          <TabList onChange={handleTabChange}>
            <Tab label="Fiscal Year" value="1" />
            <Tab label="Weekends/Holidays" value="2" />
          </TabList>
        </Box>
        <TabPanel value="1" sx={styles.tabContainer}>
          <UsageStatisticsFiscalYear
            fiscalYear={fiscalYear}
          />
        </TabPanel>
        <TabPanel value="2" sx={styles.tabContainer}>
          <UsageStatisticsPriorityPeriods
            fiscalYear={fiscalYear}
          />
        </TabPanel>
      </TabContext>
    </Box>
  )

  // #endregion

}

export default UsageStatistics;